import React, { useState, useEffect } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Stack,
  Paper,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  addContactoProveedor,
  updateContactoProveedor,
} from "../../api/ProveedoresyFabricas/proveedoresApi";

const ACCENT = "#90b2b7";
const ACCENT_HOVER = "#7a9ea3";

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 2 },
};

const ProveedoresContactsDrawer = ({
  open,
  onClose,
  proveedor,
  contacto,
  onSave,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isEditing = Boolean(contacto);
  const [formData, setFormData] = useState({
    Nombre: "",
    Puesto: "",
    Email: "",
    Telefono: "",
    Comentario: "",
    Wechat: "",
  });
  const [formError, setFormError] = useState("");

  useEffect(() => {
    setFormError("");
    if (isEditing && contacto) {
      setFormData({
        Nombre: contacto.Nombre || "",
        Puesto: contacto.Puesto || "",
        Email: contacto.Email || "",
        Telefono: contacto.Telefono || "",
        Comentario: contacto.Comentario || "",
        Wechat: contacto.Wechat || "",
      });
    } else {
      setFormData({
        Nombre: "",
        Puesto: "",
        Email: "",
        Telefono: "",
        Comentario: "",
        Wechat: "",
      });
    }
  }, [contacto, isEditing, open]);

  const handleChange = (field, value) => {
    setFormError("");
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.Nombre?.trim()) {
        setFormError(t("ProveedoresPage.errorContactNameRequired"));
        return;
      }

      if (isEditing) {
        await updateContactoProveedor(contacto.id_Contacto, formData);
      } else {
        await addContactoProveedor(proveedor.CODPROV, formData);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error("Error saving contact:", error);
      setFormError(t("ProveedoresPage.errorSaveContact"));
    }
  };

  const btnPrimarySx = {
    py: 1.1,
    fontWeight: 700,
    textTransform: "none",
    borderRadius: 2,
    bgcolor: ACCENT,
    color: "#fff",
    boxShadow: "none",
    "&:hover": { bgcolor: ACCENT_HOVER, boxShadow: "none" },
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 440 },
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          boxShadow: `-12px 0 40px ${alpha(theme.palette.common.black, 0.12)}`,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          background: `linear-gradient(125deg, ${alpha(ACCENT, 0.22)} 0%, ${alpha(
            theme.palette.background.paper,
            1,
          )} 55%, ${alpha(theme.palette.divider, 0.06)} 100%)`,
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                bgcolor: alpha(ACCENT, 0.35),
                color: theme.palette.text.primary,
              }}
            >
              {isEditing ? (
                <EditOutlinedIcon fontSize="small" />
              ) : (
                <PersonAddOutlinedIcon fontSize="small" />
              )}
            </Box>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  color: "text.secondary",
                  lineHeight: 1.2,
                }}
              >
                {t("ProveedoresPage.contactsTitle")}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.3, mt: 0.25 }}>
                {isEditing
                  ? t("ProveedoresPage.drawerEditContactTitle")
                  : t("ProveedoresPage.drawerNewContactTitle")}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {t("ProveedoresPage.drawerContactSubtitle")}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t("ProveedoresPage.closePanel")}
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              borderRadius: 2,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", px: 2.5, py: 2 }}>
        {formError ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setFormError("")}>
            {formError}
          </Alert>
        ) : null}

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            borderColor: alpha(theme.palette.divider, 0.85),
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Stack spacing={2}>
            <TextField
              label={t("ProveedoresPage.labelContactName")}
              value={formData.Nombre}
              onChange={(e) => handleChange("Nombre", e.target.value)}
              fullWidth
              required
              sx={fieldSx}
            />
            <TextField
              label={t("ProveedoresPage.labelPosition")}
              value={formData.Puesto}
              onChange={(e) => handleChange("Puesto", e.target.value)}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label={t("ProveedoresPage.labelEmailField")}
              value={formData.Email}
              onChange={(e) => handleChange("Email", e.target.value)}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label={t("ProveedoresPage.labelPhoneField")}
              value={formData.Telefono}
              onChange={(e) => handleChange("Telefono", e.target.value)}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label={t("ProveedoresPage.labelWechat")}
              value={formData.Wechat}
              onChange={(e) => handleChange("Wechat", e.target.value)}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label={t("ProveedoresPage.labelComment")}
              value={formData.Comentario}
              onChange={(e) => handleChange("Comentario", e.target.value)}
              multiline
              rows={3}
              fullWidth
              sx={fieldSx}
            />
          </Stack>
        </Paper>
      </Box>

      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          bgcolor: alpha(theme.palette.background.default, 0.65),
        }}
      >
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              flex: 1,
              py: 1.1,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 2,
              borderColor: alpha(theme.palette.text.primary, 0.2),
            }}
          >
            {t("ProveedoresPage.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ flex: 1, ...btnPrimarySx }}>
            {isEditing ? t("ProveedoresPage.saveChanges") : t("ProveedoresPage.addContactButton")}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default ProveedoresContactsDrawer;
